// Helpers for running the web app embedded inside another site (e.g. WordPress iframe).

// Persist the embed flag and parent return URLs so they survive client-side navigation within the app.
export const initEmbed = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("embed") === "1") {
      sessionStorage.setItem("pll_embed", "1");
    }
    const ret = params.get("return");
    const cancel = params.get("cancel");
    if (ret && /^https:\/\//i.test(ret)) sessionStorage.setItem("pll_embed_return", ret);
    if (cancel && /^https:\/\//i.test(cancel)) sessionStorage.setItem("pll_embed_cancel", cancel);
  } catch (e) { /* ignore */ }
};

// True when the app is rendered inside an iframe or explicitly launched in embed mode.
export const isEmbedded = () => {
  try {
    if (sessionStorage.getItem("pll_embed") === "1") return true;
    if (new URLSearchParams(window.location.search).get("embed") === "1") return true;
    return window.self !== window.top;
  } catch (e) {
    // Cross-origin access to window.top throws → we are framed.
    return true;
  }
};

// Stripe Checkout cannot render inside an iframe. When embedded, open it in a new tab;
// otherwise redirect the current window as usual.
export const openCheckout = (url) => {
  if (isEmbedded()) {
    window.open(url, "_blank", "noopener,noreferrer");
    return "new_tab";
  }
  window.location.href = url;
  return "redirect";
};

// Read the parent-site return URLs the host page passed on the embed URL
// (e.g. `?return=https://parent.example/booking-thanks`). Returns undefined
// when not embedded or not provided. Only https URLs are honoured.
export const getEmbedReturnUrls = () => {
  try {
    const ret = sessionStorage.getItem("pll_embed_return") || undefined;
    const cancel = sessionStorage.getItem("pll_embed_cancel") || undefined;
    return { returnUrl: ret, cancelUrl: cancel };
  } catch (e) {
    return {};
  }
};

// When embedded, continuously report the document height to the parent window so the
// host page (e.g. WordPress) can resize the iframe and avoid an inner scrollbar.
export const initEmbedAutoResize = () => {
  if (!isEmbedded() || typeof window === "undefined" || window.parent === window) return;

  let last = 0;
  const post = () => {
    const height = Math.ceil(
      Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight
      )
    );
    if (Math.abs(height - last) > 2) {
      last = height;
      window.parent.postMessage({ type: "pll-embed-height", height }, "*");
    }
  };

  // Report on mount and shortly after (fonts/images/layout settling).
  post();
  [150, 500, 1200].forEach((t) => setTimeout(post, t));

  if (typeof ResizeObserver !== "undefined") {
    const ro = new ResizeObserver(() => post());
    ro.observe(document.documentElement);
    ro.observe(document.body);
  }
  const mo = new MutationObserver(() => post());
  mo.observe(document.body, { childList: true, subtree: true, attributes: true, characterData: true });
  window.addEventListener("resize", post);
  window.addEventListener("load", post);
};
